import { OctagonMinus } from 'lucide-react';
import type { ReactNode } from 'react';
import { KpiRail } from '@/components/data/KpiRail';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useFilters } from '@/lib/filters';
import { formatMetric } from '@/lib/format';
import { useT, useViewMessages } from '@/lib/i18n';
import type { MethodologyId } from '@/lib/methodology';
import { useFunnel, useMetrics } from '@/lib/queries';

const MESSAGES = {
  nb: {
    titleAll: 'Funnel',
    titleLead: 'Lead-funnel',
    titleBooking: 'Booking-funnel',
    sub: 'Annonser → prøvetime → betalende medlem, for valgt periode.',
    ofPrev: 'av forrige',
  },
  en: {
    titleAll: 'Funnel',
    titleLead: 'Lead funnel',
    titleBooking: 'Booking funnel',
    sub: 'Ads → trial → paying member, for the selected range.',
    ofPrev: 'of previous',
  },
};

export function FunnelPage() {
  const m = useViewMessages(MESSAGES);
  const t = useT();
  const { segment } = useFilters();
  const metricsQuery = useMetrics();
  const funnelQuery = useFunnel();

  const title =
    segment === 'lead' ? m.titleLead : segment === 'booking' ? m.titleBooking : m.titleAll;

  if (funnelQuery.isLoading || metricsQuery.isLoading)
    return (
      <PageFrame title={title} sub={m.sub}>
        <LoadingState />
      </PageFrame>
    );
  if (funnelQuery.isError || !funnelQuery.data || !metricsQuery.data)
    return (
      <PageFrame title={title} sub={m.sub}>
        <ErrorState onRetry={() => funnelQuery.refetch()} />
      </PageFrame>
    );

  const funnel = funnelQuery.data;
  const segmentNote = 'segment' in funnel ? funnel.segmentNote : null;
  const stages = funnel.stages;
  const hasData = stages.some((s) => s.value > 0);
  const maxValue = stages.reduce((mx, s) => Math.max(mx, s.value), 0) || 1;
  const hardStopCaveat: MethodologyId = segment === 'lead' ? 'leadHandoff' : 'bookingIsMeta';

  // In segment mode the honest funnel.segmentNote is the sub-line (the 3-stage
  // Meta-only view is NOT the full ads→member funnel).
  const sub: ReactNode =
    segment === 'all' ? (
      m.sub
    ) : (
      <span className="inline-flex items-start gap-1.5">
        {segmentNote}
        <CaveatRef id="segmentHardStop" />
      </span>
    );

  return (
    <PageFrame title={title} sub={sub}>
      {!hasData ? (
        <EmptyState title={t.noData()}>{t.noDataHint()}</EmptyState>
      ) : (
        <>
          <KpiRail metrics={metricsQuery.data} funnel={funnel} segment={segment} />
          <Card className="flex flex-col gap-2 p-3 sm:p-4">
            {stages.map((stage) => {
              const widthPct = Math.max(3, (stage.value / maxValue) * 100);
              return (
                <div
                  key={stage.key}
                  className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
                >
                  <div className="w-full text-left text-label text-fg-muted sm:w-36 sm:shrink-0 sm:text-right">
                    <span className="inline-flex items-center gap-1">
                      {stage.hardStop ? (
                        <OctagonMinus className="size-3.5 text-bad" aria-hidden />
                      ) : null}
                      {stage.label}
                    </span>
                  </div>
                  <div className="relative h-9 flex-1 overflow-hidden rounded-md bg-surface-2">
                    {/* pure bar fill */}
                    <div
                      className="h-full rounded-md bg-brand/80"
                      style={{ width: `${widthPct}%` }}
                    />
                    {/* value over the track — always readable, never clipped by bar width */}
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-label font-medium text-fg tabular">
                      {formatMetric(stage.value, 'int')}
                    </span>
                    {stage.hardStop ? (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2">
                        <CaveatRef id={hardStopCaveat} />
                      </span>
                    ) : null}
                  </div>
                  <div className="w-full text-left text-label text-fg-subtle tabular sm:w-32 sm:shrink-0">
                    {stage.ofPrev === null
                      ? '—'
                      : `${formatMetric(stage.ofPrev, 'pct')} ${m.ofPrev}`}
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  title,
  sub,
  children,
}: {
  title: string;
  sub: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{title}</h1>
        <p className="mt-1 text-body text-fg-muted">{sub}</p>
      </header>
      {children}
    </div>
  );
}
